import type { AnchorHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Path } from "@/router";
import { normalizeM360Href } from "@/lib/m360Paths";

type M360MarkdownLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href?: string;
  children?: ReactNode;
};

const linkClassName =
  "font-medium text-sys-brand-primary underline decoration-sys-brand-primary/40 underline-offset-2 hover:decoration-sys-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary";

export default function M360MarkdownLink({
  href,
  children,
  className,
  target: _ignoredTarget,
  rel: _ignoredRel,
  ...rest
}: M360MarkdownLinkProps) {
  if (!href) {
    return <span className={className}>{children}</span>;
  }

  const normalized = normalizeM360Href(href);

  if (normalized.kind === "blocked") {
    return <span className={className}>{children}</span>;
  }

  if (normalized.kind === "internal") {
    return (
      <Link
        to={normalized.path as Path}
        className={className ? `${linkClassName} ${className}` : linkClassName}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const isHttp = /^https?:\/\//i.test(normalized.href);

  return (
    <a
      href={normalized.href}
      className={className ? `${linkClassName} ${className}` : linkClassName}
      {...rest}
      {...(isHttp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}
